import {
  AndroidLogoIcon,
  AppleLogoIcon,
  CaretDoubleRightIcon,
} from "@phosphor-icons/react";
import { Row } from "@tanstack/react-table";
import { Button, Card, Tooltip } from "flowbite-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Table } from "../../../components/Table";
import { useDateRange } from "../../../hooks";
import { useAuth } from "../../../hooks";
import { useAppListData } from "../../../hooks/get/useAppListData";
import { useIntegrations } from "../../../hooks/get/useIntegrations";
import { checkPermission, formatNumber } from "../../../lib/utils";
import { APP_PLATFORM_LOGO_MAP, PERMISSION_MAPPER } from "../../../shared/constants";
import { IIntegrationResponse } from "../../Configurations/Integration/interface";
import AppLogo from "../../AppCountryReports/components/AppLogo";
import type { IAdmobApp } from "../../Apps/interface";

interface TopPerformingAppChartProps {
  showWithTax?: boolean;
}

function TopPerformingAppChart({
  showWithTax = false,
}: TopPerformingAppChartProps) {
  const { user } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { dateRange } = useDateRange();
  const { data, isLoading } = useAppListData({
    startDate: dateRange.startDate,
    endDate: dateRange.endDate,
    per_page: 5,
  });

  const { data: integrations } = useIntegrations();

  const connectedPlatforms = useMemo(() => {
    if (!integrations) return new Set<string>();
    const platforms = new Set<string>();
    Object.values(integrations as IIntegrationResponse).forEach((int) => {
      int.inwards?.forEach((i) => platforms.add(i.type.toLowerCase().replace(/-/g, "_")));
      int.outwards?.forEach((o) => platforms.add(o.type.toLowerCase().replace(/-/g, "_")));
    });
    return platforms;
  }, [integrations]);

  const spendPlatforms = useMemo(() => [
    { key: "google_ads" as const, label: "Google Ads", icon: APP_PLATFORM_LOGO_MAP.google_ads },
    {
      key: "apple_ads" as const, label: "Apple Ads", icon: APP_PLATFORM_LOGO_MAP.app_store,
      aliases: ["apple_search_ads"]
    },
    { key: "meta_ads" as const, label: "Meta Ads", icon: APP_PLATFORM_LOGO_MAP.meta_ads },
  ].filter((p) =>
    connectedPlatforms.size === 0 ||
    connectedPlatforms.has(p.key) ||
    p.aliases?.some((a) => connectedPlatforms.has(a))
  ), [connectedPlatforms]);

  const topApps = data?.results?.admob_apps || [];

  const hasAppDetailsReadPermission = useMemo(() => {
    return checkPermission(
      PERMISSION_MAPPER.APP_FIND_ONE,
      "modules",
      user?.user_permissions
    );
  }, [user?.user_permissions]);

  const clickableClassesForApp = useMemo(() => {
    return hasAppDetailsReadPermission
      ? "cursor-pointer transition-colors ease hover:text-primary-600"
      : "";
  }, [hasAppDetailsReadPermission]);

  // Define columns for Table.tsx
  const columns = [
    {
      id: "app_name",
      accessorKey: "app_name",
      header: t("apps.columns.name"),
      size: 250,
      minSize: 200,
      cell: ({ row }: { row: Row<IAdmobApp> }) => {
        const app = row.original;
        return (
          <div
            className={`flex items-center relative gap-2 sm:gap-3 ${clickableClassesForApp}`}
            onClick={
              clickableClassesForApp
                ? () => navigate(`/apps/${app.app_slug}`)
                : undefined
            }
          >
            <div>
              {app?.platform?.toLowerCase() === "android" ? (
                <AndroidLogoIcon weight="fill" className="w-4 h-4 text-green-400" />
              ) : app?.platform?.toLowerCase() === "ios" ? (
                <AppleLogoIcon weight="fill" className="w-4 h-4 text-gray-700 dark:text-gray-300" />
              ) : null}
            </div>
            <div className="min-w-10 w-10 min-h-10 h-10 flex justify-center items-center border border-gray-200 dark:border-neutral-700 rounded-lg overflow-hidden">
              <AppLogo
                logoUrl={app.logo_url}
                platform={app.platform}
                appName={app.app_name}
              />
            </div>
            <p className="text-sm font-semibold break-words line-clamp-3 text-gray-800 dark:text-gray-100">
              {app.app_name}
            </p>
          </div>
        );
      },
    },
    {
      id: "estimate_revenue",
      header: t("apps.columns.estimatedRevenue"),
      accessorKey: "estimate_revenue",
      cell: ({ row }: { row: Row<IAdmobApp> }) => {
        const value = row.original.estimate_revenue;
        return (
          <span className="text-sm font-semibold whitespace-nowrap text-right block text-gray-800 dark:text-gray-100">
            ${isNaN(value) ? "0.00" : formatNumber(value)}
          </span>
        );
      },
      size: 150,
      meta: { headerAlign: "right" as const },
    },
    {
      id: "in_app_purchase",
      header: t("apps.columns.inAppPurchasesOrSubscriptions"),
      accessorKey: "in_app_purchase.total_in_app_purchase_proceeds",
      cell: ({ row }: { row: Row<IAdmobApp> }) => {
        const value = row.original.in_app_purchase?.total_in_app_purchase_proceeds ?? 0;
        return (
          <span className="text-sm font-semibold whitespace-nowrap text-right block text-gray-800 dark:text-gray-100">
            ${isNaN(value) ? "0.00" : formatNumber(value)}
          </span>
        );
      },
      size: 120,
      meta: { headerAlign: "right" as const },
    },
    {
      id: "without_tax.total_cost_usd",
      header: t("apps.columns.totalCostDollar"),
      accessorKey: "without_tax.total_cost_usd",
      cell: ({ row }: { row: Row<IAdmobApp> }) => {
        const app = row.original;
        const value = showWithTax ? app.with_tax?.total_cost_usd : app.without_tax.total_cost_usd;
        const breakdown = app.spend_breakdown;
        return (
          <div className="flex flex-col items-end gap-1.5 ">
            <span className="text-sm font-semibold whitespace-nowrap text-gray-800 dark:text-gray-100">
              ${isNaN(value) ? "0.00" : formatNumber(value)}
            </span>
            {breakdown && spendPlatforms.length > 0 && (() => {
              const activeChips = spendPlatforms
                .map((p) => ({
                  ...p,
                  pVal: showWithTax ? breakdown[p.key]?.with_tax : breakdown[p.key]?.without_tax,
                }))
                .filter(({ pVal }) => pVal && pVal > 0);

              if (activeChips.length === 0) return null;

              return (
                <div className="flex items-center justify-end gap-1.5">
                  {activeChips.map(({ key, label, icon, pVal }) => (
                    <Tooltip key={key} content={`${label}: $${formatNumber(pVal)}`} placement="top">
                      <div className="flex items-center gap-0.5 cursor-default px-1 py-0.5 rounded bg-gray-100 dark:bg-neutral-700/60">
                        <img src={icon} alt={label} className="h-3 w-3 object-contain flex-shrink-0" />
                        <span className="text-[10px] font-semibold whitespace-nowrap text-gray-600 dark:text-neutral-300">
                          ${formatNumber(Math.round(pVal))}
                        </span>
                      </div>
                    </Tooltip>
                  ))}
                </div>
              );
            })()}
          </div>
        );
      },
      size: 140,
      meta: { headerAlign: "right" as const },
    },
    {
      id: "without_tax.total_cost_usd_net",
      header: t("apps.columns.netDollar"),
      accessorKey: "without_tax.total_cost_usd_net",
      cell: ({ row }: { row: Row<IAdmobApp> }) => {
        const app = row.original;
        const value = showWithTax ? app.with_tax?.total_cost_usd_net : app.without_tax.total_cost_usd_net;
        return (
          <span className="text-sm font-semibold whitespace-nowrap text-right block text-gray-800 dark:text-gray-100">
            ${isNaN(value) ? "0.00" : formatNumber(value)}
          </span>
        );
      },
      size: 120,
      meta: { headerAlign: "right" as const },
    },
  ];

  return (
    <Card
      theme={{
        root: {
          base: "flex rounded-lg border border-gray-200 bg-white  dark:border-neutral-700 dark:bg-neutral-800 relative",
          children: "flex flex-col gap-3 p-4 relative",
        },
      }}
    >
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-2">
          <AndroidLogoIcon size={20} className="text-gray-700 dark:text-gray-300" weight="duotone" />
          <h1 className="text-md font-bold text-gray-700 dark:text-gray-300">
            {t("dashboard.topApps.title")}
          </h1>
        </div>
        <Button
          size="xs"
          outline
          color="indigo"
          onClick={() => navigate("/apps")}
        >
          <div className="flex items-center gap-1">
            {t("common.seeAllApps")}
            <CaretDoubleRightIcon size={16} weight="duotone" />
          </div>
        </Button>
      </div>
      <Table
        data={topApps}
        columns={columns}
        isLoading={isLoading}
        showPagination={false}
        loadingHeight={3}
        loadingRowCount={5}
        sortByKey="estimate_revenue"
        sortOrder="desc"
        maxHeight="none"
      />
    </Card>
  );
}

export default TopPerformingAppChart;
